import { useEffect } from 'react';
import { idlePrefetchRoutes, prefetchRoute } from '@/lib/routePrefetch';

/**
 * Warms up page code in the background so navigating never shows a loading
 * placeholder:
 *  - resting the pointer on an internal link loads that page's chunk ahead of the click
 *  - once the browser is idle, the most likely next destinations are loaded
 *
 * Renders nothing.
 */
export default function RoutePrefetcher() {
  useEffect(() => {
    // A pointer that is just passing over a link would start a download it never
    // uses. Firing one request per link the pointer crosses also piles up page
    // downloads at once, which the browser cannot keep up with — so a page's code
    // is only fetched once the pointer actually rests on its link.
    const HOVER_INTENT_MS = 150;
    let pendingTimer;
    let pendingLink;

    const onPointerOver = (event) => {
      const link = event.target?.closest?.('a[href^="/"]');
      if (!link || link === pendingLink) return;
      window.clearTimeout(pendingTimer);
      pendingLink = link;
      pendingTimer = window.setTimeout(() => {
        const href = link.getAttribute('href');
        if (!href || !link.isConnected || !link.matches(':hover')) return;
        prefetchRoute(href);
      }, HOVER_INTENT_MS);
    };

    // mouseover bubbles (mouseenter does not), so one listener covers every link.
    document.addEventListener('mouseover', onPointerOver, { passive: true });

    let idleId;
    let timeoutId;
    const runIdlePrefetch = () => idlePrefetchRoutes.forEach(prefetchRoute);

    // An idle main thread is not the same as a finished page: images and data
    // are usually still arriving. So the warm-up starts only once the current
    // page has fully loaded, otherwise it competes with the page's own
    // resources for bandwidth.
    const scheduleIdlePrefetch = () => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(runIdlePrefetch, { timeout: 3000 });
      } else {
        timeoutId = window.setTimeout(runIdlePrefetch, 1200);
      }
    };

    if (document.readyState === 'complete') {
      scheduleIdlePrefetch();
    } else {
      window.addEventListener('load', scheduleIdlePrefetch, { once: true });
    }

    return () => {
      document.removeEventListener('mouseover', onPointerOver);
      window.clearTimeout(pendingTimer);
      window.removeEventListener('load', scheduleIdlePrefetch);
      if (idleId !== undefined) window.cancelIdleCallback(idleId);
      if (timeoutId !== undefined) window.clearTimeout(timeoutId);
    };
  }, []);

  return null;
}